import { FC, PropsWithChildren, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { Box, Loading } from '@thirdspacelearning/library';

import { QUERY_STATUS, useEventTracking } from '@tsl-frontend/tsl-utilities';

import ErrorActionPanel from '../components/ErrorActionPanel';

enum LaunchDarklyKeys {
    LAUNCH_DARK_INIT = 'launch_darkly_init',
}

const sx = {
    errorActionPanel: {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowY: 'auto',
    },
};

const LaunchDarklyGate: FC<PropsWithChildren> = ({ children }) => {
    const dispatch = useDispatch();

    const ldClient = useLDClient();

    const { sendErrorTracking } = useEventTracking(dispatch);

    const { status, error } = useQuery({
        queryKey: [LaunchDarklyKeys.LAUNCH_DARK_INIT],
        queryFn: async () => {
            await ldClient?.waitForInitialization();
            return true;
        },
        enabled: !!ldClient,
        retry: false,
        staleTime: Infinity,
    });

    useEffect(() => {
        // Send failure error to Datadog
        if (status === QUERY_STATUS.error) {
            sendErrorTracking(error);
        }
    }, [status]);

    return (
        <>
            {(status === QUERY_STATUS.pending || !ldClient) && (
                <Box>
                    <Loading />
                </Box>
            )}

            {status === QUERY_STATUS.error && (
                <Box sx={sx.errorActionPanel}>
                    <ErrorActionPanel />
                </Box>
            )}

            {status === QUERY_STATUS.success && children}
        </>
    );
};

export default LaunchDarklyGate;
